<script setup lang="ts">
import { OnClickOutside } from "@vueuse/components";
import type { PaginationResponseDto } from "core/dto";
import { useModal } from "shared/composables";
import { capitalize } from "vue";

import { getPaginationDefault } from "~/common/defaults/pagination.default";
import type { ShopProductsResponseDto } from "~/common/interfaces/dto/shop/shop-products-response.dto";

const route = useRoute();
const { locale } = useI18n();
const { isModalOpened, openModal, closeModal } = useModal();

const emit = defineEmits(["update:search"]);

const search = ref({
	query: route.query.search?.toString() || "",
	isLoading: false
});

const products = ref<ShopProductsResponseDto[]>([]);
const updateSearch = async (s: string) => {
	search.value = {
		query: s,
		isLoading: true
	};

	const res = await useNuxtApp().$apiFetch<
		PaginationResponseDto<ShopProductsResponseDto>
	>("/shop/products", {
		query: {
			...getPaginationDefault(),
			limit: 9,
			locale: locale.value,
			search: search.value.query
		}
	});

	search.value.isLoading = false;
	products.value = res.data;

	if (search.value.query) {
		useTrackEvent("search", { search_term: search.value.query });
	}
};
const updateSearchAndCloseMenu = (s: string) => {
	closeModal();
	updateSearch(s);
	emit("update:search");
};
const navigate = (s: string) => {
	navigateTo(getShopLocation(s));
};
const getShopLocation = (name: string) => {
	const to: any = {
		path: "/shop"
	};
	if (name) {
		to.query = {
			search: name
		};
	}

	return to;
};

watch(() => route.query, () => {
	search.value.query = route.query.search?.toString() || "";
});
watch(locale, () => {
	updateSearch(search.value.query);
});

const debouncedUpdateSearch = useDebounceFn(updateSearch, 250);
</script>

<template>
	<OnClickOutside @trigger="closeModal">
		<div class="relative">
			<HomeInput
				:model-value="search.query"
				icon-name="solar:magnifer-outline"
				:placeholder="capitalize($t('inputSearchProductsPlaceholder'))"
				@update:model-value="debouncedUpdateSearch"
				@focus="openModal"
				@enter="(s) => {
					updateSearchAndCloseMenu(s);
					navigate(s);
				}"
			/>
			<ul
				v-show="isModalOpened && search && products.length"
				class="mt-1 py-1 absolute z-50 w-full bg-white text-sm text-green-dark border border-gray rounded-xs"
			>
				<li v-for="({ id, name }) in products" :key="`${locale}:${id}`">
					<NuxtLink
						class="block w-full py-1 px-3 hover:underline"
						:to="getShopLocation(name)"
						@click="updateSearchAndCloseMenu(name)"
					>
						{{ name }}
					</NuxtLink>
				</li>
			</ul>
		</div>
	</OnClickOutside>
</template>
